/**
 * Page "Mes consommations de crédits" — accessible aux Cooper/Membre/Expert.
 * Détail complet : filtres période, KPIs, top actions, timeline paginée.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DatePickerFr } from '../components/ui/DatePickerFr';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Coins, TrendingDown, TrendingUp, Sparkles, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MesConsommations = () => {
  const navigate = useNavigate();
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState({ items: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, tlRes] = await Promise.all([
        axios.get(`${API}/api/credits/me/usage`, {
          params: { date_from: dateFrom, date_to: dateTo },
          headers: getAuthHeader(),
        }),
        axios.get(`${API}/api/credits/me/usage/timeline`, {
          params: { date_from: dateFrom, date_to: dateTo, limit, skip },
          headers: getAuthHeader(),
        }),
      ]);
      setSummary(sumRes.data);
      setTimeline(tlRes.data);
    } catch {
      toast.error('Erreur lors du chargement de vos consommations');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, skip]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!timeline?.items?.length) return;
    const header = 'date,action_code,action_label,plan_nom,categorie,montant,type_transaction,description\n';
    const rows = timeline.items.map((t) =>
      [t.created_at, t.action_code, t.action_label, t.plan_nom, t.categorie,
        t.montant, t.type_transaction, t.description]
        .map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`)
        .join(',')
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mes-consommations-${dateFrom}_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const totaux = summary?.totaux || {};
  const solde = summary?.solde || {};
  const topActions = summary?.top_actions || [];
  const pack = summary?.suggestions?.pack_recommande;

  return (
    <div className="container mx-auto px-4 py-8 space-y-6" data-testid="mes-consommations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="mc-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Coins className="h-6 w-6 text-indigo-500" /> Mes consommations de crédits
            </h1>
            <p className="text-sm text-slate-500">
              Suivez en détail vos crédits émis, consommés, et l'usage par action.
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-base px-3 py-1">
          Solde : {solde.total ?? 0} cr.
        </Badge>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Du</Label>
              <DatePickerFr value={dateFrom} onChange={(v) => { setSkip(0); setDateFrom(v); }} className="w-full" id="mc-date-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <DatePickerFr value={dateTo} onChange={(v) => { setSkip(0); setDateTo(v); }} className="w-full" id="mc-date-to" />
            </div>
            <Button onClick={load} disabled={loading} variant="outline" data-testid="mc-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Rafraîchir
            </Button>
            <Button onClick={exportCSV} disabled={!timeline?.items?.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mc-export-csv">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
            <div className="text-right text-xs text-slate-500">
              {summary?.periode?.label}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Consommés</p>
          <p className="text-2xl font-bold text-red-500">{totaux.credits_consommes || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Émis</p>
          <p className="text-2xl font-bold text-emerald-600">{totaux.credits_emis || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Transactions</p>
          <p className="text-2xl font-bold text-slate-700">{totaux.nb_transactions || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Actions distinctes</p>
          <p className="text-2xl font-bold text-indigo-600">{totaux.nb_actions_distinctes || 0}</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top actions */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top actions consommatrices</CardTitle>
            <CardDescription>Vos usages les plus fréquents sur la période</CardDescription>
          </CardHeader>
          <CardContent>
            {topActions.length === 0 ? (
              <p className="text-sm text-slate-400 italic text-center py-4">Aucune action sur la période.</p>
            ) : (
              <ul className="space-y-2">
                {topActions.map((a) => (
                  <li key={a.code} className="flex items-center justify-between gap-2 text-sm px-3 py-2 rounded bg-slate-50">
                    <span className="truncate">
                      <span className="font-medium text-slate-700">{a.libelle}</span>{' '}
                      <span className="text-slate-400 text-xs">({a.code} · ×{a.nb_operations})</span>
                    </span>
                    <Badge>{a.credits_consommes} cr.</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Suggestion */}
        <Card className="border-l-4 border-l-indigo-500" data-testid="mc-suggestion-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-500" /> Suggestion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-slate-600">
              À votre rythme, vous consommerez environ{' '}
              <strong className="text-indigo-700">{summary?.suggestions?.besoin_estime_mois ?? 0}</strong>{' '}
              crédits par mois.
            </p>
            {pack ? (
              <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-100">
                <p className="text-xs text-indigo-700 font-medium">Pack recommandé</p>
                <p className="font-bold text-indigo-900">{pack.nom}</p>
                <p className="text-xs text-indigo-700">
                  {pack.credits} crédits {pack.prix_eur ? `pour ${pack.prix_eur} €` : ''}
                </p>
                {!pack.couvre_le_besoin && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Note : pack le plus grand disponible, mais il manquera ~{pack.manque_estime - pack.credits} crédits pour couvrir tout le mois.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-emerald-600">
                Votre solde actuel couvre votre rythme de consommation. 🎉
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Historique détaillé</CardTitle>
          <CardDescription>{timeline.total} transaction(s) sur la période</CardDescription>
        </CardHeader>
        <CardContent>
          {timeline.items.length === 0 ? (
            <p className="text-sm text-slate-400 italic text-center py-4">Aucune transaction.</p>
          ) : (
            <>
              <div className="border rounded-lg divide-y" data-testid="mc-timeline">
                {timeline.items.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {Number(t.montant || 0) < 0 ? (
                        <TrendingDown className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      ) : (
                        <TrendingUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-700 truncate">
                          {t.action_label || t.description || t.type_transaction}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">
                          {t.created_at?.slice(0, 19).replace('T', ' ')}
                          {t.plan_nom && ` · ${t.plan_nom}`}
                          {t.action_code && ` · ${t.action_code}`}
                        </p>
                      </div>
                    </div>
                    <span className={`font-mono font-bold whitespace-nowrap ${Number(t.montant || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {Number(t.montant || 0) > 0 ? '+' : ''}{t.montant || 0} cr.
                    </span>
                  </div>
                ))}
              </div>
              {timeline.total > limit && (
                <div className="flex items-center justify-between mt-3 text-xs">
                  <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="mc-prev">
                    ← Précédent
                  </Button>
                  <span className="text-slate-500">
                    {skip + 1} - {Math.min(skip + limit, timeline.total)} sur {timeline.total}
                  </span>
                  <Button variant="outline" size="sm" disabled={skip + limit >= timeline.total} onClick={() => setSkip(skip + limit)} data-testid="mc-next">
                    Suivant →
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MesConsommations;
